import type { ActionCreator, AnyAction } from 'redux';
import type { ThunkAction } from 'redux-thunk';
import JobStore from '.';
import type { AwaitedThunkResponse, ReduxAction as UnresolvedReduxAction } from '../../types';
import type { Task } from '../../../models/types';

/**
 * A thunk for dispatching an action to queue a backend Faktory job and then store its jobToken in the JobStore.
 *
 * Expects the route called in the dispatched action to return a Task object with at least the following fields before
 * it will track the job status for NotificationHub:
 * ```
 * {
 *   jobToken: string; // The job id of the queued job.
 *   task: string; // The name of the Faktory job.
 * }
 * ```
 *
 * This is easily achieved by having the route return `res.json(queuedTask);`.
 */
export default function asyncTaskThunk<T extends Pick<Task<any>, 'jobToken' | 'task'>>(
  action: ActionCreator<UnresolvedReduxAction<T>>,
  ...args: unknown[]
): ThunkAction<void, void, never, AnyAction> {
  return async (dispatch) => {
    const response = (await dispatch(action(...args))) as any as AwaitedThunkResponse<T>;

    if (response?.value?.data?.jobToken) {
      dispatch(JobStore.actions.addJob(response.value.data));
    }
  };
}
